import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { logInWithEmailAndPassword, getSelf, checkSignUpInvitation, acceptSignUpInvitation, sendForgotPasswordEmail, checkResetPasswordToken, resetPassword } from "@/data/requests/authRequests"
import { logOut } from "@/data/http"
import { toSelf } from "@/model/Self"

export const useLogInMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: logInWithEmailAndPassword,
    onSuccess: () => {
      queryClient.invalidateQueries()
    },
  })
}

export const useLogOutMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await Promise.resolve(logOut())
    },
    onMutate: () => queryClient.cancelQueries(),
    onSuccess: () => {
      queryClient.clear()
    },
  })
}

export const useSelfQuery = () =>
  useQuery({
    queryKey: ["self"],
    queryFn: getSelf,
    select: toSelf,
    staleTime: 60_000,
  })

export const useCheckSignUpInvitationQuery = (params: { token: string; userId: string } | null) =>
  useQuery({
    queryKey: ["check-sign-up-invitation", params],
    queryFn: async () => {
      if (!params) throw new Error("missing invitation params")
      await checkSignUpInvitation(params)
      return true // TanStack Query v5 requires a non-undefined return value
    },
    enabled: params !== null,
    retry: 0,
    gcTime: 0,
    staleTime: 0,
  })

export const useSignUpMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: acceptSignUpInvitation,
    onSuccess: () => queryClient.invalidateQueries(),
  })
}

export const useSendForgotPasswordEmailMutation = () =>
  useMutation({
    mutationFn: sendForgotPasswordEmail,
  })

export const useCheckResetPasswordTokenQuery = (params: { token: string; userId: string } | null) =>
  useQuery({
    queryKey: ["check-reset-password-token", params],
    queryFn: () => {
      if (!params) throw new Error("missing reset-password params")
      return checkResetPasswordToken(params).then(() => true)
    },
    enabled: params !== null,
    retry: 0,
    gcTime: 0,
    staleTime: 0,
  })

export const useResetPasswordMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: resetPassword,
    onSuccess: () => queryClient.invalidateQueries(),
  })
}
